/**
 * PostedModal Component
 *
 * View-only modal for posted articles showing content and social media links.
 * No approval actions, just viewing the published content.
 */

import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Icon from "@mui/material/Icon";
import ReactMarkdown from "react-markdown";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function PostedModal({ open, post, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!post) return null;

  const hasSocialLinks =
    post.linkedin_post_url || post.x_post_url || post.threads_post_url || post.blog_url;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      {/* Header */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom="1px solid"
        borderColor="grey.300"
      >
        <MDBox>
          <MDTypography variant="h4" fontWeight="bold">
            {post.title}
          </MDTypography>
          {post.sourceChannel && (
            <MDTypography variant="caption" color="info" fontWeight="medium">
              {post.sourceChannel}
            </MDTypography>
          )}
        </MDBox>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </MDBox>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: isMobile ? "auto" : "600px",
        }}
      >
        {/* Left Panel: Article Content */}
        <MDBox
          sx={{
            width: isMobile ? "100%" : "60%",
            p: 3,
            overflowY: "auto",
            borderRight: isMobile ? "none" : "1px solid",
            borderColor: "grey.300",
          }}
        >
          {post.banner_image && (
            <MDBox
              component="img"
              src={post.banner_image}
              alt={post.title}
              sx={{
                width: "100%",
                borderRadius: 2,
                mb: 3,
              }}
            />
          )}

          <MDTypography variant="h6" fontWeight="medium" mb={2} color="info">
            {post.abstract}
          </MDTypography>

          <MDBox
            sx={{
              "& h1, & h2, & h3": { fontWeight: "bold", mt: 3, mb: 2 },
              "& p": { mb: 2, lineHeight: 1.6 },
              "& ul, & ol": { mb: 2, pl: 3 },
              "& code": { bgcolor: "grey.100", p: 0.5, borderRadius: 1 },
              "& pre": { bgcolor: "grey.100", p: 2, borderRadius: 1, overflow: "auto" },
            }}
          >
            <ReactMarkdown>{post.content || "No content available"}</ReactMarkdown>
          </MDBox>
        </MDBox>

        {/* Right Panel: Article Details and Social Links */}
        <MDBox
          sx={{
            width: isMobile ? "100%" : "40%",
            p: 3,
            overflowY: "auto",
            bgcolor: "grey.50",
          }}
        >
          <MDTypography variant="h5" fontWeight="bold" mb={3}>
            Published Details
          </MDTypography>

          {/* Date */}
          {post.date && (
            <MDBox mb={3}>
              <MDTypography variant="body2" fontWeight="bold" mb={1}>
                Published Date:
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </MDTypography>
            </MDBox>
          )}

          {/* Source Article Link */}
          {post.article_link && (
            <MDBox mb={3}>
              <MDTypography variant="body2" fontWeight="bold" mb={1}>
                Source Article:
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={2}>
                <a
                  href={post.article_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                >
                  {post.article_link}
                </a>
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                fullWidth
                onClick={() => window.open(post.article_link, "_blank")}
              >
                View Source Article
              </MDButton>
            </MDBox>
          )}

          {/* Social Media Links */}
          {hasSocialLinks && (
            <MDBox mb={3}>
              <MDTypography variant="body2" fontWeight="bold" mb={2}>
                Published On:
              </MDTypography>

              {post.blog_url && (
                <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" color="info">
                    article
                  </Icon>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    href={post.blog_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", p: 0 }}
                  >
                    View on Blog
                  </MDButton>
                </MDBox>
              )}

              {post.linkedin_post_url && (
                <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" color="info">
                    work
                  </Icon>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    href={post.linkedin_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", p: 0 }}
                  >
                    View on LinkedIn
                  </MDButton>
                </MDBox>
              )}

              {post.x_post_url && (
                <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" color="info">
                    tag
                  </Icon>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    href={post.x_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", p: 0 }}
                  >
                    View on X
                  </MDButton>
                </MDBox>
              )}

              {post.threads_post_url && (
                <MDBox mb={2} display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" color="info">
                    alternate_email
                  </Icon>
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    href={post.threads_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", p: 0 }}
                  >
                    View on Threads
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          )}
        </MDBox>
      </DialogContent>

      {/* Action Bar */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "grey.300",
          justifyContent: "flex-end",
        }}
      >
        <MDButton onClick={onClose} color="secondary">
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

PostedModal.propTypes = {
  open: PropTypes.bool.isRequired,
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    abstract: PropTypes.string,
    content: PropTypes.string,
    banner_image: PropTypes.string,
    article_link: PropTypes.string,
    date: PropTypes.string,
    sourceChannel: PropTypes.string,
    blog_url: PropTypes.string,
    linkedin_post_url: PropTypes.string,
    x_post_url: PropTypes.string,
    threads_post_url: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default PostedModal;
